import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { PagesFilterParams, PageType, PagesListResponse, CreatePageParams, UpdatePageParams } from '@/types/page'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum, SortOrderEnum } from '@/enums'

// API functions
import { fetchPages, fetchPage, createPage, updatePage, deletePage, bulkDeletePages, sortPage } from '@/lib/api'

type PageStateType = {
  data: PageType | null
  isLoading: boolean
  error: string | null
}

type PagesListStateType = {
  data: PagesListResponse | null
  isLoading: boolean
  error: string | null
}

type PagesStoreState = {
  page: PageStateType
  pagesList: PagesListStateType
  filters: PagesFilterParams
  selectedPageIds: string[]
  setFilters: (filters: Partial<PagesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // API functions
  fetchPages: (params?: PagesFilterParams) => Promise<PagesListResponse>
  resetPagesList: () => void

  // Blog Post operations
  getPage: (id: string) => Promise<PageType>
  createPage: (params: CreatePageParams) => Promise<PageType>
  updatePage: (id: string, params: UpdatePageParams) => Promise<PageType>
  deletePage: (id: string) => Promise<void>
  bulkDeletePages: (ids: string[]) => Promise<BulkDeleteResponse>
  sortPage: (id: string, type: SortOrderEnum) => Promise<PageType>

  // Selection operations
  selectPage: (id: string) => void
  deselectPage: (id: string) => void
  togglePageSelection: (id: string) => void
  selectAllPages: (pages: PageType[]) => void
  deselectAllPages: () => void
  isPageSelected: (id: string) => boolean
  resetPage: () => void
}

const defaultFilters: PagesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'translations.en-US.title',
  q: '',
  createdUsers: undefined,
  updatedUsers: undefined,
  createdAtStart: undefined,
  createdAtEnd: undefined,
  updatedAtStart: undefined,
  updatedAtEnd: undefined
}

const usePagesStore = create<PagesStoreState>()(
  devtools((set, get) => ({
    page: {
      data: null,
      isLoading: false,
      error: null
    },
    pagesList: {
      data: null,
      isLoading: false,
      error: null
    },
    selectedPageIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof PagesFilterParams]
          } else {
            updatedFilters[key as keyof PagesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: PagesFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value as PagesFilterParams['sortBy']
        } else if (key === 'createdUsers' || key === 'updatedUsers') {
          filters[key] = value.split(',')
        } else {
          filters[key as keyof PagesFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if ((key === 'createdUsers' || key === 'updatedUsers') && Array.isArray(value)) {
            params.set(key, value.join(','))
          } else if (key === 'isBlocked') {
            params.set(key, String(value))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    getPage: async id => {
      set(state => ({
        page: {
          ...state.page,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchPage(id)

        set(state => ({
          page: {
            ...state.page,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          page: {
            ...state.page,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    createPage: async params => {
      set(state => ({
        page: {
          ...state.page,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await createPage(params)

        set(state => ({
          page: {
            ...state.page,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          page: {
            ...state.page,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    updatePage: async (id, params) => {
      set(state => ({
        page: {
          ...state.page,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await updatePage(id, params)

        set(state => ({
          page: {
            ...state.page,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          page: {
            ...state.page,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    deletePage: async id => {
      set(state => ({
        page: {
          ...state.page,
          isLoading: true,
          error: null
        }
      }))

      try {
        await deletePage(id)

        set(state => ({
          page: {
            ...state.page,
            isLoading: false
          }
        }))
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          page: {
            ...state.page,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetPage: () => {
      set(state => ({
        page: {
          ...state.page,
          data: null,
          error: null
        }
      }))
    },

    // Selection operations
    selectPage: id => {
      set(state => {
        if (state.selectedPageIds.includes(id)) {
          return state
        }
        return { selectedPageIds: [...state.selectedPageIds, id] }
      })
    },

    deselectPage: id => {
      set(state => ({
        selectedPageIds: state.selectedPageIds.filter(pageId => pageId !== id)
      }))
    },

    togglePageSelection: id => {
      set(state => {
        if (state.selectedPageIds.includes(id)) {
          return { selectedPageIds: state.selectedPageIds.filter(pageId => pageId !== id) }
        }
        return { selectedPageIds: [...state.selectedPageIds, id] }
      })
    },

    selectAllPages: pages => {
      set(() => ({
        selectedPageIds: pages.map(page => page.id)
      }))
    },

    deselectAllPages: () => {
      set(() => ({ selectedPageIds: [] }))
    },

    isPageSelected: id => {
      return get().selectedPageIds.includes(id)
    },

    // Bulk delete operation
    bulkDeletePages: async ids => {
      set(state => ({
        page: {
          ...state.page,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeletePages({ ids })

        set(state => ({
          page: {
            ...state.page,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedPageIds: state.selectedPageIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          page: {
            ...state.page,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    fetchPages: async (params?: PagesFilterParams) => {
      const filtersToUse = params || get().filters

      set(state => ({
        pagesList: {
          ...state.pagesList,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await fetchPages(filtersToUse)

        set(state => ({
          pagesList: {
            ...state.pagesList,
            data,
            isLoading: false
          }
        }))

        return data
      } catch (error) {
        const errorMessage = getErrorMessage(error)

        set(state => ({
          pagesList: {
            ...state.pagesList,
            error: errorMessage,
            isLoading: false
          }
        }))

        throw error
      }
    },

    resetPagesList: () => {
      set(state => ({
        pagesList: {
          ...state.pagesList,
          data: null,
          error: null
        }
      }))
    },

    sortPage: async (id, type) => {
      set(() => ({
        page: {
          ...get().page,
          isLoading: true,
          error: null
        }
      }))

      try {
        const data = await sortPage(id, type)

        set(() => ({
          page: {
            ...get().page,
            isLoading: false,
            data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          page: {
            data: null,
            isLoading: false,
            error: getErrorMessage(error)
          }
        }))

        throw error
      }
    }
  }))
)

export default usePagesStore
